import { useState, useCallback, useMemo } from 'react';
import {
    VoucherTemplateElement,
    VoucherTemplateElementType,
    BackendTemplateElementDTO,
    ELEMENT_TYPES,
    createDefaultElement,
    fromBackendElement,
    toBackendElement,
} from '@starjack/entities/voucherTemplate';

/**
 * Backend stores positions in top-down coordinates (0.0-1.0 relative, y=0 is top).
 * The renderer converts to iText (bottom-left) internally.
 * No Y conversion needed between backend and CSS.
 */

/**
 * Convert pixel position to relative (0.0-1.0) position.
 * Compensates for CSS scale() on the container.
 */
export const pixelsToRelative = (px: number, containerSize: number): number => {
    if (containerSize === 0) return 0;
    return Math.max(0, Math.min(1, px / containerSize));
};

export const relativeToPixels = (
    relative: number,
    containerSize: number
): number => {
    return relative * containerSize;
};

/**
 * Clamp a value between 0 and 1.
 */
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

type EditorElementsState = {
    elements: Record<VoucherTemplateElementType, VoucherTemplateElement>;
    selectedType: VoucherTemplateElementType | null;
    isDirty: boolean;
};

/**
 * Converts backend elements (Record<ElementType, BackendDTO>) to frontend format.
 * Handles color structure conversion. No Y flip needed (backend uses top-down).
 */
const normalizeElements = (
    input: Record<string, BackendTemplateElementDTO>
): Record<VoucherTemplateElementType, VoucherTemplateElement> => {
    const result: Partial<
        Record<VoucherTemplateElementType, VoucherTemplateElement>
    > = {};

    for (const [key, dto] of Object.entries(input)) {
        const type = key as VoucherTemplateElementType;
        result[type] = fromBackendElement(type, dto);
    }

    // Pad missing elements with defaults
    for (const type of ELEMENT_TYPES) {
        if (!result[type]) {
            result[type] = createDefaultElement(type);
        }
    }

    return result as Record<VoucherTemplateElementType, VoucherTemplateElement>;
};

export const useEditorElements = (
    initialElements: Record<string, BackendTemplateElementDTO>
) => {
    const [state, setState] = useState<EditorElementsState>(() => ({
        elements: normalizeElements(initialElements),
        selectedType: null,
        isDirty: false,
    }));

    const selectedElement = useMemo(
        () => (state.selectedType ? state.elements[state.selectedType] : null),
        [state.selectedType, state.elements]
    );

    const selectElement = useCallback(
        (type: VoucherTemplateElementType | null) => {
            setState((prev) => ({ ...prev, selectedType: type }));
        },
        []
    );

    const updateElement = useCallback(
        (
            type: VoucherTemplateElementType,
            changes: Partial<VoucherTemplateElement>
        ) => {
            setState((prev) => ({
                ...prev,
                isDirty: true,
                elements: {
                    ...prev.elements,
                    [type]: { ...prev.elements[type], ...changes },
                },
            }));
        },
        []
    );

    const moveElement = useCallback(
        (type: VoucherTemplateElementType, x: number, y: number) => {
            setState((prev) => ({
                ...prev,
                isDirty: true,
                elements: {
                    ...prev.elements,
                    [type]: {
                        ...prev.elements[type],
                        x: clamp01(x),
                        y: clamp01(y),
                    },
                },
            }));
        },
        []
    );

    const resizeElement = useCallback(
        (
            type: VoucherTemplateElementType,
            w: number,
            h: number,
            x?: number,
            y?: number
        ) => {
            setState((prev) => ({
                ...prev,
                isDirty: true,
                elements: {
                    ...prev.elements,
                    [type]: {
                        ...prev.elements[type],
                        w: clamp01(w),
                        h: clamp01(h),
                        ...(x !== undefined ? { x: clamp01(x) } : {}),
                        ...(y !== undefined ? { y: clamp01(y) } : {}),
                    },
                },
            }));
        },
        []
    );

    const resetDirty = useCallback(() => {
        setState((prev) => ({ ...prev, isDirty: false }));
    }, []);

    const setElements = useCallback(
        (elements: Record<string, BackendTemplateElementDTO>) => {
            setState({
                elements: normalizeElements(elements),
                selectedType: null,
                isDirty: false,
            });
        },
        []
    );

    /**
     * Returns elements converted to backend format (color objects, iText Y).
     */
    const getBackendElements = useCallback((): Record<
        VoucherTemplateElementType,
        BackendTemplateElementDTO
    > => {
        const result: Partial<
            Record<VoucherTemplateElementType, BackendTemplateElementDTO>
        > = {};

        for (const type of ELEMENT_TYPES) {
            result[type] = toBackendElement(state.elements[type]);
        }

        return result as Record<
            VoucherTemplateElementType,
            BackendTemplateElementDTO
        >;
    }, [state.elements]);

    return {
        elements: state.elements,
        selectedType: state.selectedType,
        selectedElement,
        isDirty: state.isDirty,
        selectElement,
        updateElement,
        moveElement,
        resizeElement,
        resetDirty,
        setElements,
        getBackendElements,
    };
};
